import moment from 'moment/moment';
import {
    END_DAY,
    END_MONTH,
    END_TIME,
    END_YEAR,
    START_DAY,
    START_MONTH,
    START_TIME,
    START_YEAR,
} from 'constants/MeshbotConstant';
import { getParameterValueByName } from 'helpers/arrayUtils';

/**
 * Validates that start datetime is before end datetime for "between" value type
 * Combines date (day, month, year) and time parameters to create full datetime for comparison
 * @param {Array<{name: string, value: *}>} parameters - Array of parameter objects containing:
 *   - startTime, endTime: Time strings in HH:mm format
 *   - startDay, endDay: Day numbers (defaults to 1 if not provided)
 *   - startMonth, endMonth: Month numbers (defaults to 1 if not provided)
 *   - startYear, endYear: Year numbers (defaults to 1970 if not provided)
 * @returns {boolean} True if start datetime is before end datetime, false otherwise
 * @example
 * // Valid: start date/time is before end date/time
 * isValidDateTimeRangeBetween([
 *     { name: 'startTime', value: '09:00' },
 *     { name: 'endTime', value: '17:00' },
 *     { name: 'startDay', value: 1 },
 *     { name: 'endDay', value: 15 },
 *     { name: 'startMonth', value: 6 },
 *     { name: 'endMonth', value: 6 },
 *     { name: 'startYear', value: 2025 },
 *     { name: 'endYear', value: 2025 }
 * ]) // returns true
 *
 * // Invalid: end time is before start time on same date
 * isValidDateTimeRangeBetween([
 *     { name: 'startTime', value: '17:00' },
 *     { name: 'endTime', value: '09:00' },
 *     { name: 'startDay', value: 1 },
 *     { name: 'endDay', value: 1 }
 * ]) // returns false
 */
export function isValidDateTimeRangeBetween(parameters) {
    const startTimeStr = getParameterValueByName(parameters, START_TIME);
    const endTimeStr = getParameterValueByName(parameters, END_TIME);

    if (!startTimeStr || !endTimeStr) {
        return false;
    }

    const startDay = getParameterValueByName(parameters, START_DAY) || 1;
    const endDay = getParameterValueByName(parameters, END_DAY) || 1;
    const startMonth = getParameterValueByName(parameters, START_MONTH) || 1;
    const endMonth = getParameterValueByName(parameters, END_MONTH) || 1;
    const startYear = getParameterValueByName(parameters, START_YEAR) || 1970;
    const endYear = getParameterValueByName(parameters, END_YEAR) || 1970;

    const startDateStr = `${startYear}-${String(startMonth).padStart(2, '0')}-${String(startDay).padStart(
        2,
        '0',
    )} ${startTimeStr}`;
    const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDay).padStart(
        2,
        '0',
    )} ${endTimeStr}`;

    const startDate = moment(startDateStr, 'YYYY-MM-DD HH:mm', true);
    const endDate = moment(endDateStr, 'YYYY-MM-DD HH:mm', true);

    if (!startDate.isValid() || !endDate.isValid()) {
        return false;
    }

    return startDate.isBefore(endDate);
}
